import { RingBufferDates, RingBufferRequestType } from '@/rtk/ringBufferApi';
import { Stack, MenuItem, Typography } from '@mui/material';
import { addHours } from 'date-fns';
import { ChangeEvent, useState } from 'react';
import { LelyLoadingButton, LelySelect } from '@/common/components';
import { useAppSelector } from '@/reducers/rootReducer';
import { entityInfoSelectors } from '@/reducers/entityInfoSlice';
import { getSevenDaysAgoInFarmTime, UPCOMING_DATA_WARNING } from './utils';

const UPLOAD_OPTIONS: { value: RingBufferRequestType; label: string }[] = [
  { value: 'historical', label: 'Historical data' },
  { value: 'upcoming', label: 'Upcoming data' },
  { value: 'historicalAndUpcoming', label: 'Historical and upcoming data' },
];

type Props = {
  isLoading: boolean;
  onSubmit: (data: RingBufferDates, requestType: RingBufferRequestType) => void;
};

const RingBufferActions = ({ isLoading, onSubmit }: Props) => {
  const farmTimeZone = useAppSelector(entityInfoSelectors.selectFarmTimeZone);
  const [uploadOption, setUploadOption] = useState<RingBufferRequestType | ''>(
    '',
  );
  const [hours, setHours] = useState(1);

  const showHoursSelect =
    uploadOption === 'upcoming' || uploadOption === 'historicalAndUpcoming';
  const isDisabled = isLoading || !uploadOption;

  const handleUploadOptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUploadOption(e.target.value as RingBufferRequestType);
  };

  const handleHoursSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);

    if (!isNaN(value)) {
      setHours(value);
    }
  };

  const handleSubmit = () => {
    if (!uploadOption) {
      return;
    }

    const now = new Date();
    let fromDate: Date = new Date();
    let toDate: Date = new Date();

    if (uploadOption === 'historicalAndUpcoming') {
      fromDate = getSevenDaysAgoInFarmTime(now, farmTimeZone);
      toDate = addHours(now, hours);
    } else if (uploadOption === 'historical') {
      fromDate = getSevenDaysAgoInFarmTime(now, farmTimeZone);
      toDate = now;
    } else if (uploadOption === 'upcoming') {
      fromDate = now;
      toDate = addHours(now, hours);
    }

    onSubmit(
      {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        requestedDate: now.toISOString(),
      },
      uploadOption,
    );
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={(theme) => ({
        gap: theme.lelySpacing.SM,
      })}
    >
      <LelySelect
        data-testid="ring-buffer-select"
        value={uploadOption}
        onChange={handleUploadOptionChange}
        label="Data upload"
        placeholder="Select an option"
        helperText={uploadOption === 'upcoming' ? UPCOMING_DATA_WARNING : ''}
        sx={{ width: '320px', minWidth: '128px' }}
      >
        {UPLOAD_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </LelySelect>

      {showHoursSelect && (
        <Stack
          direction="row"
          alignItems="center"
          sx={(theme) => ({ gap: theme.lelySpacing.SM })}
        >
          <Typography variant="xs">for the next</Typography>

          <LelySelect
            data-testid="ring-buffer-hours-select"
            value={String(hours)}
            onChange={handleHoursSelect}
            label="Hours"
            placeholder="Select an option"
            sx={{ minWidth: '128px' }}
          >
            {[...Array(48)].map((_, index) => {
              const hour = String(index + 1);

              return (
                <MenuItem key={hour} value={hour}>
                  {hour}
                </MenuItem>
              );
            })}
          </LelySelect>
        </Stack>
      )}

      <LelyLoadingButton
        variant="secondary"
        autoFocus
        data-testid="send-data-request-button"
        onClick={handleSubmit}
        disabled={isDisabled}
        loading={isLoading}
        sx={{ marginLeft: 'auto' }}
      >
        Send data request
      </LelyLoadingButton>
    </Stack>
  );
};

export default RingBufferActions;
